import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { trimChar } from '@components/helpers/trim-char';
import { environment } from '@environments/environment';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { CONTEXT_HEADER, ErrorResponse, ValidationErrors } from './api-errors';
import {
  AvailableStatesResponse,
  FetchDebitsParams,
  FetchDebitsResponse,
  SendByEmailParams,
} from './api.models';

const ENDPOINTS = {
  fetchDebits: '/institutional/fetch',
  sendByEmail: '/institutional/{CONSULT_ID}',
  availableStates: '/institutional/available-states',
} as const;

export type ApiEndpoint = keyof typeof ENDPOINTS;

/** Valor emitido por `onBadRequest`: erros por campo, ou `{}` para limpar. */
export type BadRequestErrors = ValidationErrors | string | null;

/**
 * Cliente da API do site. As chamadas devolvem Promises; em caso de erro, rejeitam com
 * o `ErrorResponse` normalizado pelo `apiInterceptor`.
 *
 * Erros 4xx de validação (BadRequest) são notificados por contexto: quem chama passa um
 * `context` (enviado no header `Context`) e o formulário correspondente assina
 * `onBadRequest(context)`.
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly badRequestByContext = new Map<string, Subject<BadRequestErrors>>();
  private readonly badRequest = new Subject<BadRequestErrors>();

  /** Erros de BadRequest de um contexto. Sem contexto: os que não tinham assinante no contexto. */
  onBadRequest(context?: string): Observable<BadRequestErrors> {
    if (!context) {
      return this.badRequest.asObservable();
    }
    return this.subjectFor(context).asObservable();
  }

  /** Chamado pelo interceptor. Entrega ao contexto da requisição ou, se ninguém o assina, ao canal geral. */
  notifyBadRequest(error: ErrorResponse): void {
    const subject = error.context ? this.badRequestByContext.get(error.context) : undefined;
    if (subject?.observed) {
      subject.next(error.errors);
      return;
    }
    this.badRequest.next(error.errors);
  }

  /** Limpa os erros exibidos de um contexto (emite `{}`). */
  resetBadRequest(context?: string): void {
    if (context) {
      this.badRequestByContext.get(context)?.next({});
    }
  }

  getBaseUrl(): string {
    return environment.apiBaseUrl;
  }

  /** Monta a URL e substitui os placeholders `{CHAVE}` pelas chaves de `data` em maiúsculas. */
  getFullUrl(endpoint: ApiEndpoint, data?: object): string {
    if (!Object.hasOwn(ENDPOINTS, endpoint)) {
      throw new Error('Endpoint not available.');
    }
    let fullUrl = trimChar(this.getBaseUrl(), '/') + '/' + trimChar(ENDPOINTS[endpoint], '/');
    if (data && typeof data === 'object') {
      for (const [key, value] of Object.entries(data)) {
        fullUrl = fullUrl.replace('{' + key.toUpperCase() + '}', String(value));
      }
    }
    return fullUrl;
  }

  /** `GET /institutional/fetch`, com `data` na query string (null/undefined viram texto, como no site antigo). */
  fetchDebits(data: FetchDebitsParams, context?: string): Promise<FetchDebitsResponse> {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(data)) {
      params = params.set(key, String(value));
    }
    return firstValueFrom(
      this.http.get<FetchDebitsResponse>(this.getFullUrl('fetchDebits', data), {
        params,
        headers: contextHeaders(context),
      }),
    );
  }

  /** `PUT /institutional/{consult_id}`, com `{consult_id}` também no body. */
  sendByEmail(data: SendByEmailParams, context?: string): Promise<unknown> {
    return firstValueFrom(
      this.http.put(this.getFullUrl('sendByEmail', data), data, {
        headers: contextHeaders(context),
      }),
    );
  }

  /** `GET /institutional/available-states` (regras de UF do formulário do PDV). */
  availableStates(): Promise<AvailableStatesResponse> {
    return firstValueFrom(
      this.http.get<AvailableStatesResponse>(this.getFullUrl('availableStates')),
    );
  }

  private subjectFor(context: string): Subject<BadRequestErrors> {
    let subject = this.badRequestByContext.get(context);
    if (!subject) {
      subject = new Subject<BadRequestErrors>();
      this.badRequestByContext.set(context, subject);
    }
    return subject;
  }
}

function contextHeaders(context?: string): HttpHeaders | undefined {
  return context ? new HttpHeaders({ [CONTEXT_HEADER]: context }) : undefined;
}
